/**
 * FacultyBooking — Cohort 1 founder-facing faculty booking page.
 *
 * Shows confirmed Cohort 1 faculty as 3 large cards. Click "Book a Session"
 * → opens an inline Calendly embed in a modal (with light scrim). Click event
 * tracked server-side via /api/faculty/booking-click.
 *
 * Lives at /founder/faculty (and is also embedded inside MyCohort below the roadmap).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Calendar, X as XIcon, ChevronRight, Loader2, ExternalLink } from 'lucide-react';
import { Header } from '@/components/Header';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLES = `
.fbk{background:#FAF7F0;min-height:100vh;color:#0D1B2A;font-family:Inter,system-ui,sans-serif;}
.fbk-shell{max-width:1240px;margin:0 auto;padding:64px 32px 96px;}
.fbk-eyebrow{font-size:11px;font-weight:700;letter-spacing:.32em;text-transform:uppercase;color:#C8A84B;margin-bottom:14px;}
.fbk-h1{font-family:'Cormorant Display',serif;font-size:54px;font-weight:700;line-height:1.02;letter-spacing:-.02em;margin:0 0 14px;}
.fbk-h1 em{font-style:italic;color:#C8A84B;}
.fbk-sub{font-size:17px;line-height:1.6;color:#3A4A5C;max-width:680px;margin:0 0 40px;}

.fbk-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:22px;}
@media(max-width:980px){.fbk-grid{grid-template-columns:1fr;}.fbk-shell{padding:36px 22px 64px;}.fbk-h1{font-size:38px;}}

.fbk-card{background:#fff;border:1px solid rgba(200,168,75,.22);border-left:3px solid #C8A84B;padding:32px 30px;display:flex;flex-direction:column;gap:18px;transition:transform .25s,box-shadow .25s,border-color .25s;}
.fbk-card:hover{transform:translateY(-3px);box-shadow:0 16px 40px rgba(13,27,42,.08);border-color:rgba(200,168,75,.5);}
.fbk-card-head{display:flex;align-items:center;gap:16px;}
.fbk-portrait{width:72px;height:72px;border-radius:50%;background:rgba(200,168,75,.1);border:2px solid rgba(200,168,75,.4);display:flex;align-items:center;justify-content:center;color:#C8A84B;font-family:'Cormorant Display',serif;font-style:italic;font-weight:700;font-size:24px;flex-shrink:0;overflow:hidden;}
.fbk-portrait img{width:100%;height:100%;object-fit:cover;}
.fbk-card-name{font-family:'Cormorant Display',serif;font-size:26px;font-weight:700;line-height:1.1;letter-spacing:-.005em;margin:0 0 4px;}
.fbk-card-title{font-size:12px;color:#3A4A5C;line-height:1.45;}
.fbk-card-tagline{font-style:italic;font-family:'Cormorant Display',serif;font-size:16px;color:#0D1B2A;line-height:1.4;border-top:1px solid rgba(200,168,75,.18);padding-top:16px;}

.fbk-section-l{font-size:9px;font-weight:700;letter-spacing:.22em;text-transform:uppercase;color:#7A8190;margin-bottom:8px;}
.fbk-tags{display:flex;flex-wrap:wrap;gap:5px;}
.fbk-tag{font-size:10px;font-weight:600;color:#0D1B2A;background:rgba(200,168,75,.1);padding:4px 10px;border-radius:50px;border:1px solid rgba(200,168,75,.22);}

.fbk-sessions{display:flex;flex-direction:column;gap:8px;}
.fbk-session{background:#FAF7F0;border:1px solid rgba(200,168,75,.18);padding:13px 14px;display:flex;flex-direction:column;gap:3px;cursor:pointer;transition:border-color .2s,background .2s;}
.fbk-session:hover{border-color:#C8A84B;background:#fff;}
.fbk-session-l{font-size:13px;font-weight:600;color:#0D1B2A;line-height:1.3;}
.fbk-session-d{font-size:11px;color:#3A4A5C;line-height:1.4;}

.fbk-cta{display:inline-flex;align-items:center;justify-content:center;gap:8px;background:#0D1B2A;color:#C8A84B;font-size:11px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;padding:13px 22px;border:1px solid #0D1B2A;cursor:pointer;transition:all .2s;text-decoration:none;width:100%;margin-top:auto;}
.fbk-cta:hover{background:#C8A84B;color:#0D1B2A;border-color:#C8A84B;}
.fbk-cta:disabled{opacity:.6;cursor:wait;}

.fbk-empty{padding:60px 32px;text-align:center;color:#7A8190;}

/* MODAL */
.fbk-modal-back{position:fixed;inset:0;background:rgba(13,27,42,.85);backdrop-filter:blur(8px);z-index:1000;display:flex;align-items:center;justify-content:center;padding:32px;animation:fbkIn .25s ease-out;}
@keyframes fbkIn{from{opacity:0;}to{opacity:1;}}
.fbk-modal{position:relative;width:100%;max-width:920px;height:min(720px,calc(100vh - 64px));background:#fff;border:1px solid rgba(200,168,75,.4);box-shadow:0 24px 64px rgba(0,0,0,.4);display:flex;flex-direction:column;overflow:hidden;}
.fbk-modal-head{display:flex;align-items:center;justify-content:space-between;padding:18px 24px;border-bottom:1px solid rgba(200,168,75,.2);background:#FAF7F0;}
.fbk-modal-head-l{display:flex;align-items:center;gap:14px;}
.fbk-modal-head-name{font-family:'Cormorant Display',serif;font-size:20px;font-weight:700;line-height:1.1;color:#0D1B2A;margin:0;}
.fbk-modal-head-name em{color:#C8A84B;font-style:italic;}
.fbk-modal-head-sub{font-size:11px;letter-spacing:.04em;color:#3A4A5C;margin-top:2px;}
.fbk-modal-close{background:transparent;border:1px solid rgba(13,27,42,.2);color:#0D1B2A;width:32px;height:32px;display:flex;align-items:center;justify-content:center;cursor:pointer;border-radius:50%;transition:all .2s;}
.fbk-modal-close:hover{background:#0D1B2A;color:#FAF7F0;border-color:#0D1B2A;}
.fbk-modal-body{flex:1;overflow:hidden;background:#fff;position:relative;}
.fbk-modal-body iframe{width:100%;height:100%;border:0;}
.fbk-modal-foot{padding:11px 24px;background:#FAF7F0;border-top:1px solid rgba(200,168,75,.18);display:flex;justify-content:space-between;align-items:center;gap:14px;flex-wrap:wrap;}
.fbk-modal-foot a{font-size:11px;letter-spacing:.06em;color:#0D1B2A;text-decoration:none;display:inline-flex;gap:5px;align-items:center;}
.fbk-modal-foot a:hover{color:#C8A84B;}

@media(max-width:680px){.fbk-modal{height:calc(100vh - 32px);max-height:none;}.fbk-modal-head-name{font-size:16px;}}
`;

const FacultyCard = ({ f, onBook }) => {
  const [pickedSession, setPickedSession] = useState(f.session_types?.[0]?.id);

  return (
    <article className="fbk-card" data-testid={`fbk-card-${f.slug}`}>
      <div className="fbk-card-head">
        <div className="fbk-portrait">
          {f.photo_url ? <img src={f.photo_url} alt={f.name} onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : f.initials}
        </div>
        <div>
          <h3 className="fbk-card-name">{f.name}</h3>
          <div className="fbk-card-title">{f.title}</div>
        </div>
      </div>

      <p className="fbk-card-tagline">"{f.tagline}"</p>

      <div>
        <div className="fbk-section-l">Expertise</div>
        <div className="fbk-tags">
          {(f.expertise || []).map((e) => <span key={e} className="fbk-tag">{e}</span>)}
        </div>
      </div>

      <div>
        <div className="fbk-section-l">Pick a Session Type</div>
        <div className="fbk-sessions">
          {(f.session_types || []).map((st) => (
            <div
              key={st.id}
              className="fbk-session"
              onClick={() => setPickedSession(st.id)}
              style={pickedSession === st.id ? { borderColor: '#C8A84B', background: '#fff', borderLeft: '3px solid #C8A84B' } : {}}
              data-testid={`fbk-session-${f.slug}-${st.id}`}
            >
              <div className="fbk-session-l">{st.label}</div>
              <div className="fbk-session-d">Best for: {st.best_for}</div>
            </div>
          ))}
        </div>
      </div>

      <button
        className="fbk-cta"
        onClick={() => onBook(f, pickedSession)}
        data-testid={`fbk-book-${f.slug}`}
        disabled={!f.calendly_url}
      >
        <Calendar className="h-3.5 w-3.5" />
        {f.calendly_url ? <>Book with {f.preferred_name || f.name.split(' ')[0]}</> : 'Coming soon'}
        <ChevronRight className="h-3.5 w-3.5" />
      </button>
    </article>
  );
};

const CalendlyModal = ({ open, faculty, sessionType, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    if (open) {
      window.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
    }
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [open, onClose]);

  if (!open || !faculty) return null;

  // Calendly param to hide GDPR banner & set primary color
  const sep = faculty.calendly_url.includes('?') ? '&' : '?';
  const calendlyUrl = `${faculty.calendly_url}${sep}hide_gdpr_banner=1&primary_color=C8A84B&background_color=FAF7F0&text_color=0D1B2A`;
  const sessionLabel = (faculty.session_types || []).find((s) => s.id === sessionType)?.label;

  return (
    <div className="fbk-modal-back" onClick={onClose} data-testid="fbk-modal">
      <div className="fbk-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fbk-modal-head">
          <div className="fbk-modal-head-l">
            <div className="fbk-portrait" style={{ width: 44, height: 44, fontSize: 16 }}>
              {faculty.photo_url ? <img src={faculty.photo_url} alt={faculty.name} onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : faculty.initials}
            </div>
            <div>
              <div className="fbk-modal-head-name">Book with <em>{faculty.preferred_name || faculty.name}</em></div>
              {sessionLabel && <div className="fbk-modal-head-sub">{sessionLabel}</div>}
            </div>
          </div>
          <button className="fbk-modal-close" onClick={onClose} aria-label="Close" data-testid="fbk-modal-close">
            <XIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="fbk-modal-body">
          <iframe src={calendlyUrl} title={`Book with ${faculty.name}`} loading="lazy" allow="camera; microphone; fullscreen" />
        </div>
        <div className="fbk-modal-foot">
          <span style={{ fontSize: 11, color: '#3A4A5C' }}>Powered by Calendly · Times in your local zone</span>
          <a href={faculty.calendly_url} target="_blank" rel="noopener noreferrer" data-testid="fbk-modal-open-new">
            Open in new tab <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default function FacultyBooking({ embedded = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [faculty, setFaculty] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, faculty: null, sessionType: null });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/faculty/cohort-1`)
      .then(({ data }) => { if (!cancelled) setFaculty(data.faculty || []); })
      .catch(() => { if (!cancelled) setFaculty([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleBook = async (f, sessionType) => {
    setModal({ open: true, faculty: f, sessionType });
    // Fire-and-forget click tracking
    try {
      await axios.post(`${API}/faculty/booking-click`, {
        faculty_slug: f.slug,
        session_type_id: sessionType,
        founder_email: user?.email || 'anonymous@skiaxis.com',
        founder_name: user?.full_name || user?.name || '',
      });
    } catch { /* non-blocking */ }
  };

  const content = (
    <>
      <style>{STYLES}</style>
      <div className="fbk-shell" data-testid="faculty-booking-page">
        {!embedded && (
          <>
            <div className="fbk-eyebrow">Cohort 1 · Founder Programme</div>
            <h1 className="fbk-h1">Book your <em>faculty session</em></h1>
            <p className="fbk-sub">
              Cohort 1 founders get unlimited 30-min office hours with our confirmed faculty throughout the 6-week programme.
              Pick a session type, then choose a slot that works for you. All sessions are 1:1 and confidential.
            </p>
          </>
        )}

        {loading ? (
          <div className="fbk-empty"><Loader2 className="inline h-5 w-5 animate-spin mr-2" />Loading faculty…</div>
        ) : faculty.length === 0 ? (
          <div className="fbk-empty">Faculty roster loading — please refresh in a moment.</div>
        ) : (
          <div className="fbk-grid">
            {faculty.map((f) => (
              <FacultyCard key={f.slug} f={f} onBook={handleBook} />
            ))}
          </div>
        )}
      </div>
      <CalendlyModal open={modal.open} faculty={modal.faculty} sessionType={modal.sessionType} onClose={() => setModal({ open: false, faculty: null, sessionType: null })} />
    </>
  );

  if (embedded) return content;

  return (
    <div className="fbk">
      <Header />
      {content}
    </div>
  );
}
